"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import { ArrowUp, ArrowDown, Info } from "lucide-react";
import { ReactComponent as ChartInfoIcon } from "../../assets/icons/chart/chart-info.svg";
import styles from "../../styles/shared-module-css/healthChart.module.css";
import { convertUTCToLocal } from "../../utils/helper";
import { parseDateSafe } from "../../utils/dateFormatters";
import SeriesStatsPopover from "./health-chart/SeriesStatsPopover";
import HoverTooltip from "./Tooltip";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../ui/popover";

const READING_TYPES = {
  BLOOD_PRESSURE: "blood_pressure",
  WEIGHT: "weight",
  TEMPERATURE: "temperature",
  PULSE_OX: "pulse_ox",
  BLOOD_GLUCOSE: "blood_glucose",
};

const CONFIG = {
  thresholdValues: {
    blood_pressure: {
      systolicHigh: 140,
      systolicLow: 90,
      diastolicHigh: 90,
      diastolicLow: 60,
      heartRateHigh: 100,
      heartRateLow: 60,
    },
    weight: {
      high: 100,
      low: 40,
    },
    temperature: {
      high: 99.5,
      low: 97.0,
      criticalHigh: 101.5,
      criticalLow: 95.0,
    },
    pulse_ox: {
      oxygenHigh: 100,
      oxygenLow: 95,
      oxygenCritical: 90,
      heartRateHigh: 100,
      heartRateLow: 60,
    },
    blood_glucose: {
      high: 140,
      low: 70,
      criticalHigh: 180,
      criticalLow: 55,
      fastingHigh: 100,
      fastingLow: 70,
      postprandialHigh: 140,
    },
  },
  units: {
    blood_pressure: "mmHg",
    weight: "kg",
    temperature: "°F",
    pulse_ox: "%",
    blood_glucose: "mg/dL",
  },
  lineColors: {
    blood_pressure: {
      primary: "#1e88e5",
      secondary: "#ff8a65",
      tertiary: "#212121",
    },
    weight: {
      primary: "#4caf50",
    },
    temperature: {
      primary: "#f44336",
    },
    pulse_ox: {
      primary: "#9c27b0",
      tertiary: "#212121",
    },
    blood_glucose: {
      primary: "#ff9800",
    },
  },
};

const formatNumber = (value, precision = 1) => {
  if (value === undefined || value === null) return "_ _";
  return Number.isInteger(value) ? value.toString() : value.toFixed(precision);
};

const CustomTooltip = ({ active, payload, readingType, showAverages }) => {
  if (!active || !payload || !payload.length) return null;

  const reading = payload[0].payload;
  // If we are hovering over an empty area or just an event line without reading data attached, reading might be undefined
  if (!reading || reading.isEventSlot) return null;

  let content = null;

  switch (readingType) {
    case READING_TYPES.BLOOD_PRESSURE:
      content = (
        <>
          {/* SCRUM-955: one line, numbers only - systolic/diastolic · heart rate */}
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipValue} style={{ whiteSpace: "nowrap" }}>
              {reading.systolic}/{reading.diastolic} mmHg
              {reading.heart_rate ? ` · ${reading.heart_rate} bpm` : ""}
            </span>
          </div>
          {showAverages && (reading.average_blood_pressure?.systolic != null ||
            reading.average_heart_rate != null) && (() => {
              const month = reading.recorded_at
                ? new Date(reading.recorded_at).toLocaleString("en-US", { month: "short" }).toLowerCase()
                : "";
              return (
                <>
                  {reading.average_blood_pressure?.systolic != null &&
                    reading.average_blood_pressure?.diastolic != null && (
                      <div className={styles.tooltipRow}>
                        <span className={styles.tooltipLabel}>
                          Average BP({month}):
                        </span>
                        <span className={styles.tooltipValue}>
                          {Math.round(reading.average_blood_pressure.systolic)}/
                          {Math.round(reading.average_blood_pressure.diastolic)} mmHg
                        </span>
                      </div>
                    )}
                  {reading.average_heart_rate != null && (
                    <div className={styles.tooltipRow}>
                      <span className={styles.tooltipLabel}>
                        Average HR({month}):
                      </span>
                      <span className={styles.tooltipValue}>
                        {Math.round(reading.average_heart_rate)} bpm
                      </span>
                    </div>
                  )}
                </>
              );
            })()}
        </>
      );
      break;
    case READING_TYPES.WEIGHT:
      content = (
        <>
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipLabel}>Weight:</span>
            <span className={styles.tooltipValue}>
              {formatNumber(reading.weight, 1)} kg
            </span>
          </div>
          {reading.bmi && (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>BMI:</span>
              <span className={styles.tooltipValue}>
                {formatNumber(reading.bmi, 1)}
              </span>
            </div>
          )}
          {reading.weight_change && (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Change:</span>
              <span className={styles.tooltipValue}>
                {reading.weight_change > 0 ? "+" : ""}
                {formatNumber(reading.weight_change, 1)} kg
              </span>
            </div>
          )}
          {showAverages && reading.average_weight != null && (() => {
            const month = reading.recorded_at
              ? new Date(reading.recorded_at).toLocaleString("en-US", { month: "short" }).toLowerCase()
              : "";
            return (
              <div className={styles.tooltipRow}>
                <span className={styles.tooltipLabel}>Average ({month}):</span>
                <span className={styles.tooltipValue}>
                  {formatNumber(reading.average_weight, 1)} kg
                </span>
              </div>
            );
          })()}
        </>
      );
      break;
    case READING_TYPES.TEMPERATURE:
      content = (
        <>
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipLabel}>Temperature:</span>
            <span className={styles.tooltipValue}>
              {formatNumber(reading.temperature, 1)}°F
            </span>
          </div>
          {reading.temperature_celsius && (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Celsius:</span>
              <span className={styles.tooltipValue}>
                {formatNumber(reading.temperature_celsius, 1)}°C
              </span>
            </div>
          )}
          {reading.fever_status && (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Status:</span>
              <span className={styles.tooltipValue}>
                {reading.fever_status}
              </span>
            </div>
          )}
          {showAverages && reading.average_temperature != null && (() => {
            const month = reading.recorded_at
              ? new Date(reading.recorded_at).toLocaleString("en-US", { month: "short" }).toLowerCase()
              : "";
            return (
              <div className={styles.tooltipRow}>
                <span className={styles.tooltipLabel}>Average ({month}):</span>
                <span className={styles.tooltipValue}>
                  {formatNumber(reading.average_temperature, 1)}°F
                </span>
              </div>
            );
          })()}
        </>
      );
      break;
    case READING_TYPES.PULSE_OX:
      content = (
        <>
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipLabel}>Oxygen:</span>
            <span className={styles.tooltipValue}>{reading.oxygen}%</span>
          </div>
          {reading.heart_rate && (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Heart Rate:</span>
              <span className={styles.tooltipValue}>
                {reading.heart_rate} bpm
              </span>
            </div>
          )}
          {reading.oxygen_status && (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Status:</span>
              <span className={styles.tooltipValue}>
                {reading.oxygen_status}
              </span>
            </div>
          )}
          {showAverages && (reading.average_oxygen != null || reading.average_heart_rate != null) && (() => {
            const month = reading.recorded_at
              ? new Date(reading.recorded_at).toLocaleString("en-US", { month: "short" }).toLowerCase()
              : "";
            return (
              <>
                {reading.average_oxygen != null && (
                  <div className={styles.tooltipRow}>
                    <span className={styles.tooltipLabel}>Average OX ({month}):</span>
                    <span className={styles.tooltipValue}>
                      {Math.round(reading.average_oxygen)}%
                    </span>
                  </div>
                )}
                {reading.average_heart_rate != null && (
                  <div className={styles.tooltipRow}>
                    <span className={styles.tooltipLabel}>Average HR ({month}):</span>
                    <span className={styles.tooltipValue}>
                      {Math.round(reading.average_heart_rate)} bpm
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </>
      );
      break;
    case READING_TYPES.BLOOD_GLUCOSE:
      content = (
        <>
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipLabel}>Glucose:</span>
            <span className={styles.tooltipValue}>{reading.glucose} mg/dL</span>
          </div>
          {reading.glucose_mmol && (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>mmol/L:</span>
              <span className={styles.tooltipValue}>
                {formatNumber(reading.glucose_mmol, 1)}
              </span>
            </div>
          )}
          {reading.meal_status && (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Meal:</span>
              <span className={styles.tooltipValue}>{reading.meal_status}</span>
            </div>
          )}
          {reading.glucose_status && (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Status:</span>
              <span className={styles.tooltipValue}>
                {reading.glucose_status}
              </span>
            </div>
          )}
          {showAverages && reading.average_glucose != null && (() => {
            const month = reading.recorded_at
              ? new Date(reading.recorded_at).toLocaleString("en-US", { month: "short" }).toLowerCase()
              : "";
            return (
              <div className={styles.tooltipRow}>
                <span className={styles.tooltipLabel}>Average ({month}):</span>
                <span className={styles.tooltipValue}>
                  {Math.round(reading.average_glucose)} mg/dL
                </span>
              </div>
            );
          })()}
        </>
      );
      break;
    default:
      return null;
  }

  return (
    <div className={styles.tooltipCard} role="tooltip">
      <div className={styles.tooltipHeader}>
        <div className={styles.tooltipHeaderContent}>
          <h3 className={styles.tooltipTitle}>
            {convertUTCToLocal(reading.recorded_at)}
          </h3>
        </div>
      </div>
      <div className={styles.tooltipBody}>{content}</div>
    </div>
  );
};

const EmptyState = () => (
  <div className={styles.emptyState} role="status">
    {/* Inline illustration: soft cyan disc with a stylised line-chart glyph
        floating on a dashed baseline. Pure SVG — no asset dependency. */}
    <svg
      className={styles.emptyStateIllustration}
      width="120"
      height="120"
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="60" cy="60" r="56" fill="#E4F2F6" />
      <circle cx="60" cy="60" r="56" stroke="#C9E5EE" strokeWidth="1" />
      <line
        x1="30"
        y1="82"
        x2="90"
        y2="82"
        stroke="#48A6C4"
        strokeWidth="1.5"
        strokeDasharray="3 4"
        strokeLinecap="round"
      />
      <path
        d="M30 70 L44 56 L56 64 L72 46 L90 54"
        stroke="#0D1139"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="44" cy="56" r="3" fill="#287BDB" />
      <circle cx="56" cy="64" r="3" fill="#FF8260" />
      <circle cx="72" cy="46" r="3" fill="#287BDB" />
      <circle cx="90" cy="54" r="3" fill="#0D1139" />
    </svg>
    <h5 className={styles.emptyStateTitle}>No readings yet</h5>
    <p className={styles.emptyStateSubtitle}>
      Try a different date range, or use{" "}
      <span className={styles.emptyStateHighlight}>+ Add Reading</span> above to
      start tracking this patient.
    </p>
  </div>
);

// Diamond drawn above the plot area on top of an event line (rendered as the
// ReferenceLine's label, so recharts passes the line's viewBox). Shaped
// differently from the round BP dots; `count` > 1 adds a badge for several
// events on the same day.
const EVENT_MARKER_COLOR = "#9c27b0";
const EventMarker = ({ viewBox, count = 1, active = false }) => {
  if (!viewBox) return null;
  const { x, y } = viewBox;
  const size = active ? 6 : 5;
  const cy = y - size - 3;

  return (
    <g pointerEvents="none">
      <path
        d={`M ${x} ${cy - size} L ${x + size} ${cy} L ${x} ${cy + size} L ${x - size} ${cy} Z`}
        fill={EVENT_MARKER_COLOR}
        stroke="#ffffff"
        strokeWidth={1}
      />
      {count > 1 && (
        <g>
          <circle cx={x + size + 5} cy={cy - 2} r={5} fill={EVENT_MARKER_COLOR} />
          <text
            x={x + size + 5}
            y={cy - 2}
            fill="#ffffff"
            fontSize={7}
            fontWeight="bold"
            textAnchor="middle"
            dominantBaseline="central"
          >
            {count}
          </text>
        </g>
      )}
    </g>
  );
};

// Lists every event in the hovered marker's group (one or more events on
// the same day).
const EventTooltip = ({ events, position }) => {
  if (!events || !events.length || !position) return null;
  const isGroup = events.length > 1;

  return (
    <div
      className={styles.eventTooltip}
      style={{
        position: "fixed",
        left: `${position.openRight ? position.x + 15 : position.x - 15}px`,
        top: `${position.openBelow ? position.y + 15 : position.y}px`,
        pointerEvents: "none",
        zIndex: 1000,
        transform: `translate(${position.openRight ? "0" : "-100%"}, ${
          position.openBelow ? "0" : "-100%"
        })`,
      }}
      role="tooltip"
    >
      <div className={styles.tooltipCard}>
        <div className={styles.tooltipHeader}>
          <div className={styles.tooltipHeaderContent}>
            <h3 className={styles.tooltipTitle}>
              {isGroup
                ? `${events.length} events · ${events[0].date.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}`
                : convertUTCToLocal(events[0].date)}
            </h3>
          </div>
        </div>
        {events.map((event, i) => (
          <div
            key={event.id ?? i}
            className={styles.tooltipBody}
            style={i > 0 ? { borderTop: "1px solid #e5e7eb" } : undefined}
          >
            {isGroup && (
              <div className={styles.tooltipRow}>
                <span className={styles.tooltipLabel}>Time:</span>
                <span className={styles.tooltipValue}>
                  {event.date.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            )}
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Description:</span>
              <span className={styles.tooltipValue}>{event.description}</span>
            </div>
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Created By:</span>
              <span className={styles.tooltipValue}>{event.created_by}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// SCRUM-929: BP dot colors, matching the 6-category BP legend
// (.bpLegendDot.* in healthChart.module.css).
const BP_CATEGORY_COLORS = {
  hypotensive: "#3b82f6",
  normal: "#10b981",
  elevated: "#84cc16",
  stage1: "#f59e0b",
  stage2: "#ef4444",
  crisis: "#7f1d1d",
};

// Places a BP reading in the legend's categories (same ranges as the
// legend). Higher categories win when systolic and diastolic disagree.
const getBpCategoryColor = (reading) => {
  if (reading?.systolic == null || reading?.diastolic == null) return null;
  if (reading.systolic === "" || reading.diastolic === "") return null;
  const systolic = Number(reading?.systolic);
  const diastolic = Number(reading?.diastolic);
  if (!Number.isFinite(systolic) || !Number.isFinite(diastolic)) return null;

  if (systolic > 180 || diastolic > 120) return BP_CATEGORY_COLORS.crisis;
  if (systolic >= 140 || diastolic >= 90) return BP_CATEGORY_COLORS.stage2;
  if (systolic >= 130 || diastolic >= 80) return BP_CATEGORY_COLORS.stage1;
  if (systolic < 90 || diastolic < 60) return BP_CATEGORY_COLORS.hypotensive;
  if (systolic >= 120) return BP_CATEGORY_COLORS.elevated;
  return BP_CATEGORY_COLORS.normal;
};

// Heart-rate dot color, matching the Heart Rate legend:
// <60 Bradycardia | 60-100 Normal | >100 Tachycardia.
const getHeartRateColor = (reading) => {
  if (reading?.heart_rate == null || reading.heart_rate === "") return null;
  const heartRate = Number(reading.heart_rate);
  if (!Number.isFinite(heartRate)) return null;

  if (heartRate < 60) return BP_CATEGORY_COLORS.stage1;
  if (heartRate <= 100) return BP_CATEGORY_COLORS.normal;
  return BP_CATEGORY_COLORS.stage2;
};

/**
 * SeriesLegendItem - legend row for a single chart series (used by every
 * reading type). Renders a checkbox (toggles the series' chart line
 * visibility), the label, and an (i) info button that opens a Popover
 * with the per-series Average & Distributions + Min/Max Values.
 *
 * The percentage breakdown (Normal / Elevated / Critical) uses the shared
 * `stats.normalPercentage / abnormalPercentage / criticalPercentage`
 * because the underlying useMemo returns one set across all series. The
 * Min/Max/Median values are per-series via stats.{primary,secondary,
 * tertiary}{Min,Max,Median}.
 */
const SeriesLegendItem = ({
  label,
  color,
  visible,
  onToggle,
  stats,
  seriesKey,
  showInfoButton = true,
  checkboxColor,
  warning,
}) => {
  const minMaxKey = {
    primary: { min: stats?.min, max: stats?.max, median: stats?.median },
    secondary: {
      min: stats?.secondaryMin,
      max: stats?.secondaryMax,
      median: stats?.secondaryMedian,
    },
    tertiary: {
      min: stats?.tertiaryMin,
      max: stats?.tertiaryMax,
      median: stats?.tertiaryMedian,
    },
    previousAverages: {
      min: undefined,
      max: undefined,
      median: undefined,
    },
  }[seriesKey] || {};

  const percentages =
    seriesKey === "previousAverages"
      ? []
      : [
          {
            value: stats?.normalPercentage ?? 0,
            color: "var(--accent-lime)",
            label: "Normal",
          },
          {
            value: stats?.abnormalPercentage ?? 0,
            color: "var(--accent-amber)",
            label: "Elevated",
          },
          {
            value: stats?.criticalPercentage ?? 0,
            color: "var(--accent-red-deep)",
            label: "Critical",
          },
        ];

  const checkboxBtn = (
    <button
      type="button"
      role="checkbox"
      aria-checked={visible}
      aria-label={`Toggle ${label} line`}
      onClick={warning ? undefined : onToggle}
      disabled={!!warning}
      className={styles.seriesCheckbox}
      style={{
        backgroundColor: checkboxColor || color,
        ...(warning ? { opacity: 0.45, cursor: "not-allowed" } : {}),
      }}
    >
      {visible && (
        <svg
          className={styles.seriesCheckboxCheck}
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M3.5 8.5L6.5 11.5L12.5 5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );

  return (
    <div className={styles.legendItem} style={{ alignItems: "center", gap: 8 }}>
      {warning ? (
        <HoverTooltip content={warning} position="top">
          {checkboxBtn}
        </HoverTooltip>
      ) : (
        checkboxBtn
      )}
      <span className={styles.legendText}>{label}</span>
      {showInfoButton && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Show ${label} stats`}
              title={`${label} stats`}
              className={styles.seriesInfoBtn}
            >
              <ChartInfoIcon />
            </button>
          </PopoverTrigger>
          <PopoverContent align="center" closeOnClickOutside>
            <SeriesStatsPopover
              title={label}
              percentages={percentages}
              minMaxMedian={minMaxKey}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

// Visible time window ({ start, end } in ms, or null for "all") for a
// timeFrame preset or custom dateRange. Exported so the reading PDF can list
// exactly the events this chart draws.
export const getChartTimeWindow = (timeFrame, dateRange) => {
  const end = new Date();
  // Default end is end of today
  end.setHours(23, 59, 59, 999);

  // 1. Checking custom date range first
  if (dateRange?.startDate && dateRange?.endDate) {
    const s = new Date(dateRange.startDate);
    const e = new Date(dateRange.endDate);
    // Ensuring e captures full end day
    e.setHours(23, 59, 59, 999);
    return { start: s.getTime(), end: e.getTime() };
  }

  // 2. Checking predefined timeFrame
  const start = new Date();
  // If no timeframe is set, maybe default to 1 week or handle as "all"
  if (!timeFrame) return null;

  switch (timeFrame) {
    case '2days':
      start.setDate(end.getDate() - 2);
      break;
    case '1week':
      start.setDate(end.getDate() - 7);
      break;
    case '1month':
      start.setMonth(end.getMonth() - 1);
      break;
    case '3months':
      start.setMonth(end.getMonth() - 3);
      break;
    case '6months':
      start.setMonth(end.getMonth() - 6);
      break;
    case '1year':
      start.setFullYear(end.getFullYear() - 1);
      break;
    default:
      return null;
  }

  // Setting start to beginning of the day
  start.setHours(0, 0, 0, 0);

  return { start: start.getTime(), end: end.getTime() };
};

const HealthChart = ({
  readingType = READING_TYPES.BLOOD_PRESSURE,
  timeFrame = "1week",
  dateRange = { startDate: "", endDate: "" },
  readings = null,
  events = [],
  bpStats,
  weightStats,
  glucoseStats,
  temperatureStats,
  pulseOxStats,
  // May 2026 redesign: parent (readings.js) lifts threshold state so the
  // "Set Reading Thresholds" popover in the chart panel header can drive
  // it directly. When the parent doesn't supply thresholds (legacy callers
  // — PDF report, audit-report-share), we fall back to internal state.
  thresholds: thresholdsProp,
  setThresholds: setThresholdsProp,
}) => {
  const chartContainerRef = useRef(null);
  const infoGridRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [internalThresholds, setInternalThresholds] = useState({
    systolicLow: false,
    systolicHigh: false,
    diastolicLow: false,
    diastolicHigh: false,
    high: false,
    low: false,
    oxygenLow: false,
  });
  const thresholds = thresholdsProp ?? internalThresholds;
  const setThresholds = setThresholdsProp ?? setInternalThresholds;
  // Open-state for the per-series stats popover triggered by the (i) icons
  // in the legend below the chart. `null` means closed; otherwise it's
  // the series key currently expanded ("primary" | "secondary" | "tertiary").
  const [openStatsSeries, setOpenStatsSeries] = useState(null);
  const [visibleLines, setVisibleLines] = useState({
    primary: true,
    secondary: true,
    tertiary: true,
    // May 2026 redesign: visibility toggle for the dashed "Previous Averages"
    // overlay. The dashed lines render today regardless; this checkbox is
    // the user-facing control. Backend data for previous-period averages
    // is not yet wired (TODO), so the (i) tooltip on this legend item
    // shows "—" placeholders for now.
    previousAverages: false,
  });
  const [chartData, setChartData] = useState([]);
  const [error, setError] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  // The hovered event marker's group: { id, events } (see eventGroups).
  const [hoveredEventGroup, setHoveredEventGroup] = useState(null);
  const [eventTooltipPosition, setEventTooltipPosition] = useState(null);
  // SCRUM-954: true only while the cursor is close to an actual reading's
  // slot, so the BP tooltip doesn't open over empty space between readings.
  const [isNearReading, setIsNearReading] = useState(false);
  const chartRef = useRef(null);

  const thresholdValues = CONFIG.thresholdValues[readingType] || {};
  const unit = CONFIG.units[readingType] || "";
  const lineColors = useMemo(
    () => CONFIG.lineColors[readingType] || { primary: "#1e88e5" },
    [readingType]
  );

  useEffect(() => {
    const updateDimensions = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.clientWidth;
        const height = chartContainerRef.current.clientHeight;

        setContainerDimensions({ width, height });
        setIsSmallScreen(width < 768);
      }
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      if (chartContainerRef.current) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    setThresholds({
      systolicLow: false,
      systolicHigh: false,
      diastolicLow: false,
      diastolicHigh: false,
      high: false,
      low: false,
      oxygenLow: false,
    });

    setVisibleLines({
      primary: true,
      secondary: readingType === READING_TYPES.BLOOD_PRESSURE,
      tertiary:
        readingType === READING_TYPES.BLOOD_PRESSURE ||
        readingType === READING_TYPES.PULSE_OX,
      previousAverages: false,
    });
  }, [readingType]);

  const filterDataByDateRange = useCallback((data, dateRange) => {
    if (!data || !data.length || !dateRange.startDate || !dateRange.endDate)
      return data;

    try {
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);

      endDate.setHours(23, 59, 59, 999);

      return data.filter((item) => {
        const itemDate = new Date(item.recorded_at);
        return itemDate >= startDate && itemDate <= endDate;
      });
    } catch (error) {
      console.error("Error filtering data by date range:", error);
      return data;
    }
  }, []);

  const enrichData = useCallback((data, readingType) => {
    if (!data || !data.length) return [];

    try {
      const enrichedData = JSON.parse(JSON.stringify(data));

      enrichedData.sort(
        (a, b) => (parseDateSafe(a.recorded_at) || new Date(0)) - (parseDateSafe(b.recorded_at) || new Date(0))
      );

      // Add timestamp for numeric x-axis
      enrichedData.forEach((d) => {
        d.timestamp = (parseDateSafe(d.recorded_at) || new Date(0)).getTime();
      });

      switch (readingType) {
        case READING_TYPES.BLOOD_PRESSURE:
          enrichedData.forEach((reading, index) => {
            if (
              reading.systolic !== undefined &&
              reading.diastolic !== undefined
            ) {
              reading.pulse_pressure = reading.systolic - reading.diastolic;
            }

            if (
              !reading.mean_arterial_pressure &&
              reading.systolic !== undefined &&
              reading.diastolic !== undefined
            ) {
              reading.mean_arterial_pressure =
                Math.round(
                  (reading.diastolic +
                    (1 / 3) * (reading.systolic - reading.diastolic)) *
                    10
                ) / 10;
            }
          });
          break;

        case READING_TYPES.WEIGHT:
          enrichedData.forEach((reading, index) => {
            if (
              index > 0 &&
              reading.weight !== undefined &&
              enrichedData[index - 1].weight !== undefined
            ) {
              reading.weight_change =
                Math.round(
                  (reading.weight - enrichedData[index - 1].weight) * 10
                ) / 10;
            }

            if (reading.height && reading.weight) {
              const heightInMeters = reading.height / 100;
              reading.bmi =
                Math.round(
                  (reading.weight / (heightInMeters * heightInMeters)) * 10
                ) / 10;
            }
          });
          break;

        case READING_TYPES.TEMPERATURE:
          enrichedData.forEach((reading) => {
            if (reading.temperature !== undefined) {
              reading.temperature_celsius =
                Math.round((((reading.temperature - 32) * 5) / 9) * 10) / 10;
            }

            if (reading.temperature !== undefined) {
              if (reading.temperature >= thresholdValues.criticalHigh) {
                reading.fever_status = "High Fever";
              } else if (reading.temperature >= thresholdValues.high) {
                reading.fever_status = "Fever";
              } else if (reading.temperature < thresholdValues.low) {
                reading.fever_status = "Low";
              } else {
                reading.fever_status = "Normal";
              }
            }
          });
          break;

        case READING_TYPES.PULSE_OX:
          enrichedData.forEach((reading) => {
            if (reading.oxygen !== undefined) {
              if (reading.oxygen < thresholdValues.oxygenCritical) {
                reading.oxygen_status = "Critical";
              } else if (reading.oxygen < thresholdValues.oxygenLow) {
                reading.oxygen_status = "Low";
              } else {
                reading.oxygen_status = "Normal";
              }
            }
          });
          break;

        case READING_TYPES.BLOOD_GLUCOSE:
          enrichedData.forEach((reading) => {
            if (reading.glucose !== undefined) {
              reading.glucose_mmol =
                Math.round((reading.glucose / 18) * 10) / 10;
            }

            if (reading.glucose !== undefined) {
              if (reading.glucose > thresholdValues.criticalHigh) {
                reading.glucose_status = "Very High";
              } else if (reading.glucose > thresholdValues.high) {
                reading.glucose_status = "High";
              } else if (reading.glucose < thresholdValues.criticalLow) {
                reading.glucose_status = "Very Low";
              } else if (reading.glucose < thresholdValues.low) {
                reading.glucose_status = "Low";
              } else {
                reading.glucose_status = "Normal";
              }
            }
          });
          break;
      }

      return enrichedData;
    } catch (error) {
      console.error("Error enriching data:", error);
      setError("Error processing health data");
      return data;
    }
  }, []);

  useEffect(() => {
    try {
      if (readings && readings.length > 0) {
        let filteredData = [...readings];

        if (dateRange.startDate && dateRange.endDate) {
          filteredData = filterDataByDateRange(filteredData, dateRange);
        }

        const enrichedData = enrichData(filteredData, readingType);
        setChartData(enrichedData);
      } else {
        setChartData([]);
      }
    } catch (error) {
      console.error("Error processing chart data:", error);
      setError("Error processing chart data");
      setChartData([]);
    }
  }, [readings, readingType, dateRange, filterDataByDateRange, enrichData]);

  // --- Calculating visible time window to filter events ---
  const timeWindow = useMemo(
    () => getChartTimeWindow(timeFrame, dateRange),
    [timeFrame, dateRange]
  );

  // Data-point dots always render for ranges of one month or less. Beyond
  // that, they still render as long as the chart isn't too dense to read -
  // i.e. 40 or fewer plotted readings; past that the chart shows the line
  // only.
  const showDots = useMemo(() => {
    if (!timeWindow) return true;
    // timeWindow.end always lands on the end of a day (23:59:59.999), so a
    // plain (end - start) ms diff overshoots the real calendar-day span by
    // almost a full day — e.g. the "1 month" preset would measure as
    // ~31.9999 days and wrongly fall outside a 31-day cutoff. Stripping
    // that fixed end-of-day offset back off gives the true day count.
    const endOfDayOffsetMs = 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999;
    const daysInRange = Math.round((timeWindow.end - endOfDayOffsetMs - timeWindow.start) / (1000 * 60 * 60 * 24));
    if (daysInRange <= 31) return true;
    return chartData.length <= 40;
  }, [timeWindow, chartData]);

  // --- Filtering events strictly based on the calculated Time Window ---
  const filteredEvents = useMemo(() => {
    if (!events || !Array.isArray(events) || events.length === 0) {
      return [];
    }

    try {
      return events
        .map((event, index) => ({
          ...event,
          id: event.id || `event-${index}`,
          dateObj: event.date instanceof Date ? event.date : new Date(event.date),
        }))
        .filter((event) => {
          if (isNaN(event.dateObj.getTime())) return false;

          // If we have a time window, filter events outside of it
          if (timeWindow) {
            return (
              event.dateObj.getTime() >= timeWindow.start &&
              event.dateObj.getTime() <= timeWindow.end
            );
          }
          
          return true; 
        })
        .map(e => ({...e, date: e.dateObj})); 
    } catch (error) {
      console.error("Error filtering events:", error);
      return [];
    }
  }, [events, timeWindow]);

  // The x-axis is a category scale (see the XAxis element below), giving
  // every reading its own evenly-spaced slot regardless of the real time
  // gap to its neighbors — a handful of readings scattered across a wide
  // date range (e.g. two same-day, one a few days later, one a month later)
  // stay clearly distinguishable instead of clustering together with a
  // large empty stretch for the outlier.
  //
  // SCRUM-954: events get their own slot in that ordering, at their real
  // date/time, instead of snapping onto the nearest reading's slot (which
  // drew e.g. a Sep 27 event on a 9/29 reading). An event slot carries no
  // reading values, so lines bridge it (connectNulls) and the BP tooltip
  // stays silent there. A slot never shares a reading's timestamp, so an
  // event and a reading taken at the same moment stay separate.
  // Events on the same calendar day share one slot and one marker, so
  // several events on a day don't crowd the axis; the marker's tooltip
  // lists each of them.
  // `plotData` feeds the chart only; stats keep using `chartData`.
  const eventGroups = useMemo(() => {
    const byDay = new Map();
    filteredEvents.forEach((event) => {
      const day = event.date.toDateString();
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(event);
    });
    return [...byDay.values()].map((dayEvents) => {
      const sorted = [...dayEvents].sort((a, b) => a.date - b.date);
      return { id: sorted[0].id, events: sorted };
    });
  }, [filteredEvents]);

  const plotData = useMemo(() => {
    if (!eventGroups.length) return chartData;

    // Every slot needs a unique timestamp: if two readings share one,
    // Recharts numbers the category axis 0..n-1 instead, and the event
    // ReferenceLines (positioned by timestamp) silently disappear. A
    // duplicate is nudged by 1ms on a copy; tooltips show recorded_at.
    const usedTimestamps = new Set();
    const readingSlots = [...chartData]
      .sort((a, b) => a.timestamp - b.timestamp)
      .map((reading) => {
        let timestamp = reading.timestamp;
        while (usedTimestamps.has(timestamp)) timestamp += 1;
        usedTimestamps.add(timestamp);
        return timestamp === reading.timestamp
          ? reading
          : { ...reading, timestamp };
      });

    const eventSlots = eventGroups.map((group) => {
      let timestamp = group.events[0].date.getTime();
      while (usedTimestamps.has(timestamp)) timestamp += 1;
      usedTimestamps.add(timestamp);
      return { timestamp, isEventSlot: true, eventGroupId: group.id };
    });

    return [...readingSlots, ...eventSlots].sort(
      (a, b) => a.timestamp - b.timestamp
    );
  }, [chartData, eventGroups]);

  const eventSlotTimestamps = useMemo(() => {
    const byGroupId = {};
    plotData.forEach((d) => {
      if (d.isEventSlot) byGroupId[d.eventGroupId] = d.timestamp;
    });
    return byGroupId;
  }, [plotData]);

  const hasData = useMemo(() => {
    return (
      (chartData && Array.isArray(chartData) && chartData.length > 0) ||
      (filteredEvents && Array.isArray(filteredEvents) && filteredEvents.length > 0)
    );
  }, [chartData, filteredEvents]);

  // With a lot of closely-spaced readings (e.g. a 6-month/1-year window),
  // "monotone" interpolation bends sharply at every point and reads as
  // jagged/spiky. "natural" draws a smoother curve through the exact same
  // points (it doesn't alter any reading's value, only how the line flows
  // between them) — only worth it once there are enough points for the
  // jaggedness to actually show.
  const lineCurveType = chartData.length > 30 ? "natural" : "monotone";

  const previousAverageWarning = useMemo(() => {
    if (!readings || !Array.isArray(readings) || readings.length === 0) return "";
    return readings[0]?.is_short_range_warning === true
      ? "Date range selected must be greater than a month."
      : "";
  }, [readings]);

  useEffect(() => {
    if (previousAverageWarning) {
      setVisibleLines((prev) => ({ ...prev, previousAverages: false }));
    }
  }, [previousAverageWarning]);

  // Handle mouse move for event tooltips
 const handleChartMouseMove = useCallback(
    (e) => {
      if (!chartRef.current || eventGroups.length === 0 || plotData.length === 0) {
        setHoveredEventGroup(null);
        setEventTooltipPosition(null);
        return;
      }

      const rect = chartRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const chartWidth = rect.width;
      const marginLeft = 60; // YAxis width (default 60) + margin.left (0)
      const marginRight = 10; // margin.right (10)
      const chartAreaWidth = chartWidth - marginLeft - marginRight;

      if (chartAreaWidth <= 0) return;

      // A LineChart category axis is a point scale: slots run edge to edge,
      // the first on the left axis and the last on the right edge, so slot i
      // sits at i / (n - 1) of the chart area (a lone slot sits centered).
      const slotX = (index) =>
        plotData.length === 1
          ? marginLeft + chartAreaWidth / 2
          : marginLeft + (index / (plotData.length - 1)) * chartAreaWidth;

      // Finding the closest event line
      let closestGroup = null;
      let minDistance = Infinity;

      // Kept tight so the user is hovering the line / its marker, which is
      // about 12px wide.
      const HIT_THRESHOLD = 6;

      eventGroups.forEach((group) => {
        const slotIndex = plotData.findIndex(
          (d) => d.isEventSlot && d.eventGroupId === group.id
        );
        if (slotIndex === -1) return;

        const eventX = slotX(slotIndex);

        const distance = Math.abs(mouseX - eventX);

        // hovering if within HIT_THRESHOLD of the line
        if (distance < HIT_THRESHOLD && distance < minDistance) {
          minDistance = distance;
          closestGroup = group;
        }
      });

      if (closestGroup && minDistance < HIT_THRESHOLD) {
        setHoveredEventGroup(closestGroup);
        
        // Using cursor position (e.clientX/Y) instead of line position
        // This allows us to place the tooltip relative to the mouse
        setEventTooltipPosition({
          x: e.clientX,
          y: e.clientY,
          // Open toward the roomier side so the card isn't cut off at the
          // chart's left edge or the top of the viewport.
          openRight: mouseX < chartWidth / 2,
          openBelow: e.clientY < window.innerHeight / 2,
        });
      } else {
        setHoveredEventGroup(null);
        setEventTooltipPosition(null);
      }
    },
    [eventGroups, plotData]
  );

  const handleChartMouseLeave = useCallback(() => {
    setHoveredEventGroup(null);
    setEventTooltipPosition(null);
    setIsNearReading(false);
  }, []);

  // Recharts always activates the slot nearest the cursor, however far away
  // it is. Only treat it as a hover when the cursor is within
  // READING_HIT_THRESHOLD px of that slot and it holds a reading (not an
  // event slot). Dense charts, where slots are closer than 2x the threshold,
  // behave as before since the nearest slot is always within range.
  const READING_HIT_THRESHOLD = 8;
  const handleLineChartMouseMove = useCallback(
    (state) => {
      const slot =
        state?.activeTooltipIndex != null
          ? plotData[state.activeTooltipIndex]
          : null;
      const slotX = state?.activeCoordinate?.x;

      setIsNearReading(
        Boolean(
          state?.isTooltipActive &&
            slot &&
            !slot.isEventSlot &&
            slotX != null &&
            Math.abs(state.chartX - slotX) <= READING_HIT_THRESHOLD
        )
      );
    },
    [plotData]
  );

  const isAbnormal = useCallback((reading, readingType) => {
    const thresholds = CONFIG.thresholdValues[readingType];
    if (!thresholds) return false;

    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        return (
          reading.systolic > thresholds.systolicHigh ||
          reading.systolic < thresholds.systolicLow ||
          reading.diastolic > thresholds.diastolicHigh ||
          reading.diastolic < thresholds.diastolicLow
        );
      case READING_TYPES.WEIGHT:
        return (
          reading.weight > thresholds.high || reading.weight < thresholds.low
        );
      case READING_TYPES.TEMPERATURE:
        return (
          reading.temperature > thresholds.high ||
          reading.temperature < thresholds.low
        );
      case READING_TYPES.PULSE_OX:
        return reading.oxygen < thresholds.oxygenLow;
      case READING_TYPES.BLOOD_GLUCOSE:
        if (reading.meal_status === "Fasting") {
          return (
            reading.glucose > thresholds.fastingHigh ||
            reading.glucose < thresholds.fastingLow
          );
        } else if (reading.meal_status === "Postprandial") {
          return reading.glucose > thresholds.postprandialHigh;
        }
        return (
          reading.glucose > thresholds.high || reading.glucose < thresholds.low
        );
      default:
        return false;
    }
  }, []);

  const isCritical = useCallback((reading, readingType) => {
    const thresholds = CONFIG.thresholdValues[readingType];
    if (!thresholds) return false;

    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        return (
          reading.systolic > thresholds.systolicHigh + 20 ||
          reading.systolic < thresholds.systolicLow - 10 ||
          reading.diastolic > thresholds.diastolicHigh + 10 ||
          reading.diastolic < thresholds.diastolicLow - 10
        );
      case READING_TYPES.TEMPERATURE:
        return (
          reading.temperature > thresholds.criticalHigh ||
          reading.temperature < thresholds.criticalLow
        );
      case READING_TYPES.PULSE_OX:
        return reading.oxygen < thresholds.oxygenCritical;
      case READING_TYPES.BLOOD_GLUCOSE:
        return (
          reading.glucose > thresholds.criticalHigh ||
          reading.glucose < thresholds.criticalLow
        );
      default:
        return false;
    }
  }, []);

  // Hover-dot color: BP uses its legend category color (SCRUM-929); other
  // types keep the normal / abnormal / critical colors.
  const getActiveDotColor = useCallback(
    (reading) => {
      if (readingType === READING_TYPES.BLOOD_PRESSURE) {
        const bpColor = getBpCategoryColor(reading);
        if (bpColor) return bpColor;
      }
      if (isCritical(reading, readingType)) return "#ef4444";
      if (isAbnormal(reading, readingType)) return "#f59e0b";
      return "#10b981";
    },
    [readingType, isAbnormal, isCritical]
  );

  // Systolic / diastolic dots colored by the reading's BP category, so they
  // match the legend; falls back to the line color if a value is missing.
  const renderBpDot = useCallback(
    (fallbackColor) => (props) => {
      const { cx, cy, payload, key } = props;
      if (cx == null || cy == null) return null;
      return (
        <circle
          key={key ?? `${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r={3}
          fill={getBpCategoryColor(payload) || fallbackColor}
          strokeWidth={0}
        />
      );
    },
    []
  );

  const primaryDataKey = useMemo(() => {
    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        return "systolic";
      case READING_TYPES.WEIGHT:
        return "weight";
      case READING_TYPES.TEMPERATURE:
        return "temperature";
      case READING_TYPES.PULSE_OX:
        return "oxygen";
      case READING_TYPES.BLOOD_GLUCOSE:
        return "glucose";
      default:
        return "value";
    }
  }, [readingType]);

  const secondaryDataKey = useMemo(() => {
    return readingType === READING_TYPES.BLOOD_PRESSURE ? "diastolic" : null;
  }, [readingType]);

  const tertiaryDataKey = useMemo(() => {
    return readingType === READING_TYPES.BLOOD_PRESSURE ||
      readingType === READING_TYPES.PULSE_OX
      ? "heart_rate"
      : null;
  }, [readingType]);

  const yAxisDomain = useMemo(() => {
    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        return [40, 180];
      case READING_TYPES.WEIGHT:
        if (chartData && chartData.length > 0) {
          const weights = chartData
            .map((d) => d.weight)
            .filter((w) => w !== undefined && w !== null);
          if (weights.length === 0) return [0, 150];
          // Include any enabled threshold values so their reference lines
          // stay inside the visible Y-range. Weight is the only type with a
          // data-driven domain; the others use a fixed domain that already
          // contains their thresholds.
          const wt = CONFIG.thresholdValues.weight || {};
          const bounds = [...weights];
          if (thresholds.low && wt.low != null) bounds.push(wt.low);
          if (thresholds.high && wt.high != null) bounds.push(wt.high);
          const min = Math.min(...bounds);
          const max = Math.max(...bounds);
          const padding = (max - min) * 0.1;
          return [Math.max(0, min - padding), max + padding];
        }
        return [0, 150];
      case READING_TYPES.TEMPERATURE:
        return [95, 105];
      case READING_TYPES.PULSE_OX:
        return [85, 100];
      case READING_TYPES.BLOOD_GLUCOSE:
        return [50, 200];
      default:
        return [0, 200];
    }
  }, [readingType, chartData, thresholds]);

  const stats = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return {
        avg: 0,
        min: 0,
        max: 0,
        median: 0,
        stdDev: 0,
        normalCount: 0,
        abnormalCount: 0,
        criticalCount: 0,
        normalPercentage: 0,
        abnormalPercentage: 0,
        criticalPercentage: 0,
        trend: 0,
        secondaryAvg: 0,
        tertiaryAvg: 0,
      };
    }

    try {
      const values = chartData
        .map((d) => d[primaryDataKey])
        .filter((v) => v !== undefined && v !== null);

      const avg =
        values.length > 0
          ? values.reduce((sum, val) => sum + val, 0) / values.length
          : 0;
      const min = values.length > 0 ? Math.min(...values) : 0;
      const max = values.length > 0 ? Math.max(...values) : 0;

      const sortedValues = [...values].sort((a, b) => a - b);
      const median =
        sortedValues.length % 2 === 0
          ? (sortedValues[sortedValues.length / 2 - 1] +
              sortedValues[sortedValues.length / 2]) /
            2
          : sortedValues[Math.floor(sortedValues.length / 2)];

      const variance =
        values.length > 0
          ? values.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) /
            values.length
          : 0;
      const stdDev = Math.sqrt(variance);

      // Add these calculations after the primary values calculations and before secondaryAvg calculation
      let secondaryMin = 0,
        secondaryMax = 0,
        secondaryMedian = 0;
      if (secondaryDataKey) {
        const secondaryValues = chartData
          .map((d) => d[secondaryDataKey])
          .filter((v) => v !== undefined && v !== null);
        if (secondaryValues.length > 0) {
          secondaryMin = Math.min(...secondaryValues);
          secondaryMax = Math.max(...secondaryValues);
          const sortedSecondaryValues = [...secondaryValues].sort(
            (a, b) => a - b
          );
          secondaryMedian =
            sortedSecondaryValues.length % 2 === 0
              ? (sortedSecondaryValues[sortedSecondaryValues.length / 2 - 1] +
                  sortedSecondaryValues[sortedSecondaryValues.length / 2]) /
                2
              : sortedSecondaryValues[
                  Math.floor(sortedSecondaryValues.length / 2)
                ];
        }
      }

      let tertiaryMin = 0,
        tertiaryMax = 0,
        tertiaryMedian = 0;
      if (tertiaryDataKey) {
        const tertiaryValues = chartData
          .map((d) => d[tertiaryDataKey])
          .filter((v) => v !== undefined && v !== null);
        if (tertiaryValues.length > 0) {
          tertiaryMin = Math.min(...tertiaryValues);
          tertiaryMax = Math.max(...tertiaryValues);
          const sortedTertiaryValues = [...tertiaryValues].sort(
            (a, b) => a - b
          );
          tertiaryMedian =
            tertiaryValues.length % 2 === 0
              ? (sortedTertiaryValues[sortedTertiaryValues.length / 2 - 1] +
                  sortedTertiaryValues[sortedTertiaryValues.length / 2]) /
                2
              : sortedTertiaryValues[
                  Math.floor(sortedTertiaryValues.length / 2)
                ];
        }
      }

      let secondaryAvg = 0;
      if (secondaryDataKey) {
        const secondaryValues = chartData
          .map((d) => d[secondaryDataKey])
          .filter((v) => v !== undefined && v !== null);
        secondaryAvg =
          secondaryValues.length > 0
            ? secondaryValues.reduce((sum, val) => sum + val, 0) /
              secondaryValues.length
            : 0;
      }

      let tertiaryAvg = 0;
      if (tertiaryDataKey) {
        const tertiaryValues = chartData
          .map((d) => d[tertiaryDataKey])
          .filter((v) => v !== undefined && v !== null);
        tertiaryAvg =
          tertiaryValues.length > 0
            ? tertiaryValues.reduce((sum, val) => sum + val, 0) /
              tertiaryValues.length
            : 0;
      }

      const total = chartData.length;
      const normalCount = chartData.filter(
        (d) => !isAbnormal(d, readingType) && !isCritical(d, readingType)
      ).length;
      const abnormalCount = chartData.filter(
        (d) => isAbnormal(d, readingType) && !isCritical(d, readingType)
      ).length;
      const criticalCount = chartData.filter((d) =>
        isCritical(d, readingType)
      ).length;

      const normalPercentage =
        total > 0 ? Math.round((normalCount / total) * 100) : 0;
      const abnormalPercentage =
        total > 0 ? Math.round((abnormalCount / total) * 100) : 0;
      const criticalPercentage =
        total > 0 ? Math.round((criticalCount / total) * 100) : 0;

      let trend = 0;
      if (values.length >= 2) {
        const firstValue = values[0];
        const lastValue = values[values.length - 1];
        trend = (lastValue - firstValue) / values.length;
      }

      return {
        avg: Math.round(avg * 10) / 10,
        min,
        max,
        median: Math.round(median * 10) / 10,
        stdDev: Math.round(stdDev * 100) / 100,
        secondaryAvg: Math.round(secondaryAvg * 10) / 10,
        secondaryMin,
        secondaryMax,
        secondaryMedian: Math.round(secondaryMedian * 10) / 10,
        tertiaryAvg: Math.round(tertiaryAvg * 10) / 10,
        tertiaryMin,
        tertiaryMax,
        tertiaryMedian: Math.round(tertiaryMedian * 10) / 10,
        normalCount,
        abnormalCount,
        criticalCount,
        normalPercentage,
        abnormalPercentage,
        criticalPercentage,
        trend,
      };
    } catch (error) {
      console.error("Error calculating statistics:", error);
      return {
        avg: 0,
        min: 0,
        max: 0,
        median: 0,
        stdDev: 0,
        normalCount: 0,
        abnormalCount: 0,
        criticalCount: 0,
        normalPercentage: 0,
        abnormalPercentage: 0,
        criticalPercentage: 0,
        trend: 0,
        secondaryAvg: 0,
        tertiaryAvg: 0,
      };
    }
  }, [
    chartData,
    primaryDataKey,
    secondaryDataKey,
    tertiaryDataKey,
    readingType,
    isAbnormal,
    isCritical,
  ]);

  // Dotted reference lines for the previous-period averages (Logic 4).
  // Gated on the "Previous Averages" toggle and hidden for short date
  // ranges (the toggle is disabled then and previousAverageWarning is set).
  // The backend attaches the average_* fields only to a subset of readings,
  // so scan for the first reading that carries each value. Each line is
  // drawn in its series colour.
  const previousAverageLines = useMemo(() => {
    if (!visibleLines.previousAverages || previousAverageWarning) return [];
    if (!chartData || chartData.length === 0) return [];
    const lines = [];
    const firstVal = (getter) => {
      for (const d of chartData) {
        const v = getter(d);
        if (v != null && !Number.isNaN(Number(v))) return Number(v);
      }
      return null;
    };
    const push = (y, key, color) => {
      if (y != null) lines.push({ y, key, color });
    };
    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        push(firstVal((d) => d.average_blood_pressure?.systolic), "avgSystolic", lineColors.primary);
        push(firstVal((d) => d.average_blood_pressure?.diastolic), "avgDiastolic", lineColors.secondary);
        push(firstVal((d) => d.average_heart_rate), "avgHeartRate", lineColors.tertiary);
        break;
      case READING_TYPES.WEIGHT:
        push(firstVal((d) => d.average_weight), "avgWeight", lineColors.primary);
        break;
      case READING_TYPES.BLOOD_GLUCOSE:
        push(firstVal((d) => d.average_glucose), "avgGlucose", lineColors.primary);
        break;
      case READING_TYPES.TEMPERATURE:
        push(firstVal((d) => d.average_temperature), "avgTemperature", lineColors.primary);
        break;
      case READING_TYPES.PULSE_OX:
        push(firstVal((d) => d.average_oxygen), "avgOxygen", lineColors.primary);
        push(firstVal((d) => d.average_heart_rate), "avgHeartRate", lineColors.tertiary);
        break;
      default:
        break;
    }
    return lines;
  }, [
    visibleLines.previousAverages,
    previousAverageWarning,
    chartData,
    readingType,
    lineColors,
  ]);

  const thresholdLines = useMemo(() => {
    const lines = [];
    const thresholdConfig = CONFIG.thresholdValues[readingType];

    if (!thresholdConfig) return lines;

    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        if (thresholds.systolicHigh) {
          lines.push({
            y: thresholdConfig.systolicHigh,
            label: `Systolic High: ${thresholdConfig.systolicHigh}`,
            key: "systolicHigh",
          });
        }
        if (thresholds.systolicLow) {
          lines.push({
            y: thresholdConfig.systolicLow,
            label: `Systolic Low: ${thresholdConfig.systolicLow}`,
            key: "systolicLow",
          });
        }
        if (thresholds.diastolicHigh) {
          lines.push({
            y: thresholdConfig.diastolicHigh,
            label: `Diastolic High: ${thresholdConfig.diastolicHigh}`,
            key: "diastolicHigh",
          });
        }
        if (thresholds.diastolicLow) {
          lines.push({
            y: thresholdConfig.diastolicLow,
            label: `Diastolic Low: ${thresholdConfig.diastolicLow}`,
            key: "diastolicLow",
          });
        }
        break;
      case READING_TYPES.WEIGHT:
        if (thresholds.high) {
          lines.push({
            y: thresholdConfig.high,
            label: `Weight High: ${thresholdConfig.high}`,
            key: "weightHigh",
          });
        }
        if (thresholds.low) {
          lines.push({
            y: thresholdConfig.low,
            label: `Weight Low: ${thresholdConfig.low}`,
            key: "weightLow",
          });
        }
        break;
      case READING_TYPES.TEMPERATURE:
        if (thresholds.high) {
          lines.push({
            y: thresholdConfig.high,
            label: `Fever: ${thresholdConfig.high}°F`,
            key: "tempHigh",
          });
        }
        if (thresholds.low) {
          lines.push({
            y: thresholdConfig.low,
            label: `Low: ${thresholdConfig.low}°F`,
            key: "tempLow",
          });
        }
        break;
      case READING_TYPES.PULSE_OX:
        if (thresholds.oxygenLow) {
          lines.push({
            y: thresholdConfig.oxygenLow,
            label: `Oxygen Low: ${thresholdConfig.oxygenLow}%`,
            key: "oxygenLow",
          });
        }
        break;
      case READING_TYPES.BLOOD_GLUCOSE:
        if (thresholds.high) {
          lines.push({
            y: thresholdConfig.high,
            label: `Glucose High: ${thresholdConfig.high}`,
            key: "glucoseHigh",
          });
        }
        if (thresholds.low) {
          lines.push({
            y: thresholdConfig.low,
            label: `Glucose Low: ${thresholdConfig.low}`,
            key: "glucoseLow",
          });
        }
        break;
    }

    return lines;
  }, [readingType, thresholds]);

  const referenceAreas = useMemo(() => {
    const areas = [];
    const thresholdConfig = CONFIG.thresholdValues[readingType];

    if (!thresholdConfig) return areas;

    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        if (thresholds.systolicHigh) {
          areas.push({
            y1: thresholdConfig.systolicHigh,
            y2: 180,
            key: "systolicHighArea",
          });
        }
        if (thresholds.systolicLow) {
          areas.push({
            y1: 40,
            y2: thresholdConfig.systolicLow,
            key: "systolicLowArea",
          });
        }
        break;
      case READING_TYPES.TEMPERATURE:
        if (thresholds.high) {
          areas.push({
            y1: thresholdConfig.high,
            y2: 105,
            key: "tempHighArea",
          });
        }
        if (thresholds.low) {
          areas.push({
            y1: 95,
            y2: thresholdConfig.low,
            key: "tempLowArea",
          });
        }
        break;
      case READING_TYPES.PULSE_OX:
        if (thresholds.oxygenLow) {
          areas.push({
            y1: 85,
            y2: thresholdConfig.oxygenLow,
            key: "oxygenLowArea",
          });
        }
        break;
      case READING_TYPES.BLOOD_GLUCOSE:
        if (thresholds.high) {
          areas.push({
            y1: thresholdConfig.high,
            y2: 200,
            key: "glucoseHighArea",
          });
        }
        if (thresholds.low) {
          areas.push({
            y1: 50,
            y2: thresholdConfig.low,
            key: "glucoseLowArea",
          });
        }
        break;
    }

    return areas;
  }, [readingType, thresholds]);

  const toggleThreshold = useCallback((type) => {
    setThresholds((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, []);

  const toggleLine = useCallback((line) => {
    setVisibleLines((prev) => ({
      ...prev,
      [line]: !prev[line],
    }));
  }, []);

  // Legend rows for the current reading type. Every type leads with the
  // "Previous Averages" placeholder, then its own series. BP keeps its
  // existing slightly-darker checkbox colors; other types use their exact
  // line color so the checkbox matches the line on the chart.
  const legendItems = useMemo(() => {
    const items = [
      {
        label: "Previous Averages",
        seriesKey: "previousAverages",
        checkboxColor: "#48a6c4",
        showInfoButton: false,
      },
    ];
    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        items.push(
          { label: "Systolic", seriesKey: "primary", checkboxColor: "#287bdb" },
          { label: "Diastolic", seriesKey: "secondary", checkboxColor: "#ff8260" },
          { label: "Heart Rate", seriesKey: "tertiary", checkboxColor: "#1e1e1e" }
        );
        break;
      case READING_TYPES.WEIGHT:
        items.push({
          label: "Weight",
          seriesKey: "primary",
          checkboxColor: lineColors.primary,
        });
        break;
      case READING_TYPES.BLOOD_GLUCOSE:
        items.push({
          label: "Glucose",
          seriesKey: "primary",
          checkboxColor: lineColors.primary,
        });
        break;
      case READING_TYPES.TEMPERATURE:
        items.push({
          label: "Temperature",
          seriesKey: "primary",
          checkboxColor: lineColors.primary,
        });
        break;
      case READING_TYPES.PULSE_OX:
        items.push(
          {
            label: "Oxygen",
            seriesKey: "primary",
            checkboxColor: lineColors.primary,
          },
          { label: "Heart Rate", seriesKey: "tertiary", checkboxColor: "#1e1e1e" }
        );
        break;
      default:
        break;
    }
    return items;
  }, [readingType, lineColors]);

  const renderTrendIcon = useCallback((trend, isInverted = false) => {
    const adjustedTrend = isInverted ? -trend : trend;

    if (Math.abs(adjustedTrend) < 0.05) {
      return (
        <span
          className={styles.trendStable}
          style={{ color: "#64748b" }}
          aria-label="Stable"
        >
          —
        </span>
      );
    } else if (adjustedTrend > 0) {
      return (
        <ArrowUp
          className={styles.trendIcon}
          size={14}
          style={{ color: "#ef4444" }}
          aria-label="Increasing"
        />
      );
    } else {
      return (
        <ArrowDown
          className={styles.trendIcon}
          size={14}
          style={{ color: "#10b981" }}
          aria-label="Decreasing"
        />
      );
    }
  }, []);

  const readingTypeTitle = useMemo(() => {
    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        return "Blood Pressure";
      case READING_TYPES.WEIGHT:
        return "Weight";
      case READING_TYPES.TEMPERATURE:
        return "Temperature";
      case READING_TYPES.PULSE_OX:
        return "Pulse Oximetry";
      case READING_TYPES.BLOOD_GLUCOSE:
        return "Blood Glucose";
      default:
        return "Readings";
    }
  }, [readingType]);

  const renderThresholdCheckboxes = useCallback(() => {
    const thresholdConfig = CONFIG.thresholdValues[readingType];
    if (!thresholdConfig) return null;

    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        return (
          <>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="systolicLow"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.systolicLow}
                onChange={() => toggleThreshold("systolicLow")}
                aria-label="Show systolic low threshold"
              />
              <label className={styles.checkboxLabel}>
                Systolic Low ({thresholdConfig.systolicLow} mmHg)
              </label>
            </div>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="systolicHigh"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.systolicHigh}
                onChange={() => toggleThreshold("systolicHigh")}
                aria-label="Show systolic high threshold"
              />
              <label className={styles.checkboxLabel}>
                Systolic High ({thresholdConfig.systolicHigh} mmHg)
              </label>
            </div>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="diastolicLow"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.diastolicLow}
                onChange={() => toggleThreshold("diastolicLow")}
                aria-label="Show diastolic low threshold"
              />
              <label className={styles.checkboxLabel}>
                Diastolic Low ({thresholdConfig.diastolicLow} mmHg)
              </label>
            </div>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="diastolicHigh"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.diastolicHigh}
                onChange={() => toggleThreshold("diastolicHigh")}
                aria-label="Show diastolic high threshold"
              />
              <label className={styles.checkboxLabel}>
                Diastolic High ({thresholdConfig.diastolicHigh} mmHg)
              </label>
            </div>
          </>
        );
      case READING_TYPES.WEIGHT:
        return (
          <>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="weightLow"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.low}
                onChange={() => toggleThreshold("low")}
                aria-label="Show weight low threshold"
              />
              <label className={styles.checkboxLabel}>
                Weight Low ({thresholdConfig.low} kg)
              </label>
            </div>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="weightHigh"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.high}
                onChange={() => toggleThreshold("high")}
                aria-label="Show weight high threshold"
              />
              <label className={styles.checkboxLabel}>
                Weight High ({thresholdConfig.high} kg)
              </label>
            </div>
          </>
        );
      case READING_TYPES.TEMPERATURE:
        return (
          <>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="tempLow"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.low}
                onChange={() => toggleThreshold("low")}
                aria-label="Show temperature low threshold"
              />
              <label className={styles.checkboxLabel}>
                Low Temp ({thresholdConfig.low}°F)
              </label>
            </div>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="tempHigh"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.high}
                onChange={() => toggleThreshold("high")}
                aria-label="Show temperature high threshold"
              />
              <label className={styles.checkboxLabel}>
                Fever ({thresholdConfig.high}°F)
              </label>
            </div>
          </>
        );
      case READING_TYPES.PULSE_OX:
        return (
          <div className={styles.checkboxItem}>
            <input
              type="checkbox"
              id="oxygenLow"
              className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
              checked={thresholds.oxygenLow}
              onChange={() => toggleThreshold("oxygenLow")}
              aria-label="Show oxygen low threshold"
            />
            <label className={styles.checkboxLabel}>
              Oxygen Low ({thresholdConfig.oxygenLow}%)
            </label>
          </div>
        );
      case READING_TYPES.BLOOD_GLUCOSE:
        return (
          <>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="glucoseLow"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.low}
                onChange={() => toggleThreshold("low")}
                aria-label="Show glucose low threshold"
              />
              <label className={styles.checkboxLabel}>
                Glucose Low ({thresholdConfig.low} mg/dL)
              </label>
            </div>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="glucoseHigh"
                className={`${styles.checkbox} ${styles.thresholdCheckbox}`}
                checked={thresholds.high}
                onChange={() => toggleThreshold("high")}
                aria-label="Show glucose high threshold"
              />
              <label className={styles.checkboxLabel}>
                Glucose High ({thresholdConfig.high} mg/dL)
              </label>
            </div>
          </>
        );
      default:
        return null;
    }
  }, [readingType, thresholds, toggleThreshold]);

  const renderLineCheckboxes = useCallback(() => {
    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        return (
          <>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="systolic"
                className={`${styles.checkbox} ${styles.systolicCheckbox}`}
                checked={visibleLines.primary}
                onChange={() => toggleLine("primary")}
                aria-label="Show systolic line"
              />
              <div className={styles.dataLineLabel}>
                <label htmlFor="systolic" className={styles.checkboxLabel}>
                  Systolic
                </label>
              </div>
            </div>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="diastolic"
                className={`${styles.checkbox} ${styles.diastolicCheckbox}`}
                checked={visibleLines.secondary}
                onChange={() => toggleLine("secondary")}
                aria-label="Show diastolic line"
              />
              <div className={styles.dataLineLabel}>
                <label htmlFor="diastolic" className={styles.checkboxLabel}>
                  Diastolic
                </label>
              </div>
            </div>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="heartRate"
                className={`${styles.checkbox} ${styles.heartRateCheckbox}`}
                checked={visibleLines.tertiary}
                onChange={() => toggleLine("tertiary")}
                aria-label="Show heart rate line"
              />
              <div className={styles.dataLineLabel}>
                <label htmlFor="heartRate" className={styles.checkboxLabel}>
                  Heart Rate
                </label>
              </div>
            </div>
          </>
        );
      case READING_TYPES.PULSE_OX:
        return (
          <>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="oxygen"
                className={`${styles.checkbox} ${styles.systolicCheckbox}`}
                checked={visibleLines.primary}
                onChange={() => toggleLine("primary")}
                aria-label="Show oxygen line"
              />
              <div className={styles.dataLineLabel}>
                <label htmlFor="oxygen" className={styles.checkboxLabel}>
                  Oxygen
                </label>
              </div>
            </div>
            <div className={styles.checkboxItem}>
              <input
                type="checkbox"
                id="heartRate"
                className={`${styles.checkbox} ${styles.heartRateCheckbox}`}
                checked={visibleLines.tertiary}
                onChange={() => toggleLine("tertiary")}
                aria-label="Show heart rate line"
              />
              <div className={styles.dataLineLabel}>
                <label htmlFor="heartRate" className={styles.checkboxLabel}>
                  Heart Rate
                </label>
              </div>
            </div>
          </>
        );
      default:
        return (
          <div className={styles.checkboxItem}>
            <input
              type="checkbox"
              id="primary"
              className={`${styles.checkbox} ${styles.systolicCheckbox}`}
              checked={visibleLines.primary}
              onChange={() => toggleLine("primary")}
              aria-label={`Show ${readingTypeTitle.toLowerCase()} line`}
            />
            <div className={styles.dataLineLabel}>
              <label htmlFor="primary" className={styles.checkboxLabel}>
                {readingType === READING_TYPES.WEIGHT
                  ? "Weight"
                  : readingType === READING_TYPES.TEMPERATURE
                  ? "Temperature"
                  : readingType === READING_TYPES.BLOOD_GLUCOSE
                  ? "Glucose"
                  : "Value"}
              </label>
            </div>
          </div>
        );
    }
  }, [readingType, visibleLines, toggleLine, readingTypeTitle]);

  const renderStatsDisplay = useCallback(() => {
    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        return (
          <>
            <div className={styles.statsItem}>
              <div className={styles.statsLabelWithTrend}>
                <span className={styles.statsLabel}>Systolic:</span>
                {renderTrendIcon(stats.trend, true)}
              </div>
              <span className={styles.statsValue}>{stats.avg} mmHg</span>
            </div>
            <div className={styles.statsDistribution}>
              <div className={styles.distributionLabels}>
                <span className={styles.normalLabel}>
                  <span
                    className={styles.normalLegendDot}
                    aria-hidden="true"
                  ></span>
                  ({stats.normalPercentage}%)
                </span>
                <span className={styles.abnormalLabel}>
                  <span
                    className={styles.abnormalLegendDot}
                    aria-hidden="true"
                  ></span>
                  ({stats.abnormalPercentage}%)
                </span>
                <span className={styles.criticalLabel}>
                  <span
                    className={styles.criticalLegendDot}
                    aria-hidden="true"
                  ></span>
                  ({stats.criticalPercentage}%)
                </span>
              </div>
            </div>

            <div className={styles.statsItem}>
              <div className={styles.statsLabelWithTrend}>
                <span className={styles.statsLabel}>Diastolic:</span>
                {renderTrendIcon(stats.trend, true)}
              </div>
              <span className={styles.statsValue}>
                {stats.secondaryAvg} mmHg
              </span>
            </div>
            <div className={styles.statsDistribution}>
              <div className={styles.distributionLabels}>
                <span className={styles.normalLabel}>
                  <span
                    className={styles.normalLegendDot}
                    aria-hidden="true"
                  ></span>{" "}
                  ({stats.normalPercentage}%)
                </span>
                <span className={styles.abnormalLabel}>
                  <span
                    className={styles.abnormalLegendDot}
                    aria-hidden="true"
                  ></span>{" "}
                  ({stats.abnormalPercentage}%)
                </span>
                <span className={styles.criticalLabel}>
                  <span
                    className={styles.criticalLegendDot}
                    aria-hidden="true"
                  ></span>{" "}
                  ({stats.criticalPercentage}%)
                </span>
              </div>
            </div>

            <div className={styles.statsItem}>
              <div className={styles.statsLabelWithTrend}>
                <span className={styles.statsLabel}>Heart Rate:</span>
                {renderTrendIcon(stats.trend, false)}
              </div>
              <span className={styles.statsValue}>{stats.tertiaryAvg} bpm</span>
            </div>
          </>
        );
      default:
        return (
          <>
            <div className={styles.statsItem}>
              <div className={styles.statsLabelWithTrend}>
                <span className={styles.statsLabel}>
                  {readingType === READING_TYPES.WEIGHT
                    ? "Weight"
                    : readingType === READING_TYPES.TEMPERATURE
                    ? "Temperature"
                    : readingType === READING_TYPES.PULSE_OX
                    ? "Oxygen"
                    : readingType === READING_TYPES.BLOOD_GLUCOSE
                    ? "Glucose"
                    : "Value"}
                  :
                </span>
                {renderTrendIcon(
                  stats.trend,
                  readingType === READING_TYPES.BLOOD_PRESSURE
                )}
              </div>
              <span className={styles.statsValue}>
                {stats.avg} {unit}
              </span>
            </div>
            <div className={styles.statsDistribution}>
              <div className={styles.distributionLabels}>
                <span className={styles.normalLabel}>
                  <span
                    className={styles.normalLegendDot}
                    aria-hidden="true"
                  ></span>{" "}
                  ({stats.normalPercentage}%)
                </span>
                <span className={styles.abnormalLabel}>
                  <span
                    className={styles.abnormalLegendDot}
                    aria-hidden="true"
                  ></span>{" "}
                  ({stats.abnormalPercentage}%)
                </span>
                <span className={styles.criticalLabel}>
                  <span
                    className={styles.criticalLegendDot}
                    aria-hidden="true"
                  ></span>{" "}
                  ({stats.criticalPercentage}%)
                </span>
              </div>
            </div>
          </>
        );
    }
  }, [readingType, stats, unit, renderTrendIcon]);

  const renderMinMaxDisplay = useCallback(() => {
    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        return (
          <div className={styles.minMaxContainer}>
            <div>
              <h4 className={styles.minMaxTitle}>Systolic</h4>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Min:</span>
                <span className={styles.minMaxValue}>{stats.min}</span>
              </div>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Max:</span>
                <span className={styles.minMaxValue}>{stats.max}</span>
              </div>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Median:</span>
                <span className={styles.minMaxValue}>{stats.median}</span>
              </div>
            </div>
            <div>
              <h4 className={styles.minMaxTitle}>Diastolic</h4>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Min:</span>
                <span className={styles.minMaxValue}>
                  {stats.secondaryMin || 0}
                </span>
              </div>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Max:</span>
                <span className={styles.minMaxValue}>
                  {stats.secondaryMax || 0}
                </span>
              </div>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Median:</span>
                <span className={styles.minMaxValue}>
                  {stats.secondaryMedian || 0}
                </span>
              </div>
            </div>
            <div>
              <h4 className={styles.minMaxTitle}>Heart Rate</h4>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Min:</span>
                <span className={styles.minMaxValue}>
                  {stats.tertiaryMin || 0}
                </span>
              </div>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Max:</span>
                <span className={styles.minMaxValue}>
                  {stats.tertiaryMax || 0}
                </span>
              </div>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Median:</span>
                <span className={styles.minMaxValue}>
                  {stats.tertiaryMedian || 0}
                </span>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className={styles.minMaxContainer}>
            <div>
              <h4 className={styles.minMaxTitle}>
                {readingType === READING_TYPES.WEIGHT
                  ? "Weight"
                  : readingType === READING_TYPES.TEMPERATURE
                  ? "Temperature"
                  : readingType === READING_TYPES.PULSE_OX
                  ? "Oxygen"
                  : readingType === READING_TYPES.BLOOD_GLUCOSE
                  ? "Glucose"
                  : "Value"}
              </h4>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Min:</span>
                <span className={styles.minMaxValue}>{stats.min}</span>
              </div>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Max:</span>
                <span className={styles.minMaxValue}>{stats.max}</span>
              </div>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Median:</span>
                <span className={styles.minMaxValue}>{stats.median}</span>
              </div>
              <div className={styles.minMaxItem}>
                <span className={styles.minMaxLabel}>Std Dev:</span>
                <span className={styles.minMaxValue}>{stats.stdDev}</span>
              </div>
            </div>
          </div>
        );
    }
  }, [readingType, stats]);

  const renderSummary = useCallback(() => {
    if (!hasData) {
      return null;
    }

    switch (readingType) {
      case READING_TYPES.BLOOD_PRESSURE:
        if (!bpStats) return null;

        const bpCategoriesMeta = {
          "Hypotensive": { label: "Hypotensive", range: "<90 or <60", color: "hypotensive" },
          normal: { label: "Normal", range: "90-120 and <80", color: "normal" },
          elevated: {
            label: "Elevated",
            range: "120-129",
            color: "elevated",
          },
          stage1: {
            label: "Stage 1 Hypertension",
            range: "130-139 or 80-89",
            color: "stage1",
          },
          stage2: {
            label: "Stage 2 Hypertension",
            range: "≥140 or ≥90",
            color: "stage2",
          },
          "Hypertensive Crisis": { label: "Hypertensive Crisis", range: ">180 and/or >120", color: "crisis" },
        };

        // Heart Rate categories metadata
        const hrCategoriesMeta = {
          bradycardia: {
            label: "Bradycardia",
            range: "<60 bpm",
            color: "stage1",
          },
          normal: {
            label: "Normal",
            range: "60-100 bpm",
            color: "normal",
          },
          tachycardia: {
            label: "Tachycardia",
            range: ">100 bpm",
            color: "stage2",
          },
        };

        const bpOrder = ["Hypotensive", "normal", "elevated", "stage1", "stage2", "Hypertensive Crisis"];

        const bpCategoryPercentages = bpOrder.map((key) => {
          const percentage = bpStats?.categories?.[key] || 0;
          const drivers = bpStats?.category_drivers?.[key];
          // Diastolic is the remainder so Sys + Dia always equals the rounded category %
          const totalShown = Number(percentage.toFixed(0));
          const systolicShown = drivers
            ? Math.min(Number((drivers.systolic || 0).toFixed(0)), totalShown)
            : null;
          return {
            key,
            percentage,
            count: Math.round(percentage),
            drivers: drivers
              ? { systolic: systolicShown, diastolic: totalShown - systolicShown }
              : null,
            ...bpCategoriesMeta[key],
          };
        });

        const bpHeartRateCategoryPercentages = Object.entries(
          bpStats?.heart_rate_categories || {}
        )?.map(([key, percentage]) => ({
          key,
          percentage,
          count: Math.round(percentage),
          ...hrCategoriesMeta[key],
        }));

        return (
          <div className={styles.bpSummaryRow}>
            <div className={styles.bpSummarySection}>
              <div className={styles.bpSummaryHeader}>
                <h3 className={styles.bpSummaryTitle}>
                  Average Blood Pressure
                </h3>
                <div className={styles.bpAverageValue}>
                  {bpStats?.average?.systolic !== null
                    ? bpStats?.average?.systolic.toFixed(0)
                    : "--"}
                  /
                  {bpStats?.average?.diastolic !== null
                    ? bpStats?.average?.diastolic.toFixed(0)
                    : "--"}{" "}
                  mmHg
                </div>
              </div>

              <div className={styles.bpCategoryBar}>
                <div className={styles.bpBarContainer}>
                  {bpCategoryPercentages?.map(
                    (category) =>
                      category.percentage > 0 && (
                        <div
                          key={category.key}
                          className={`${styles.bpBarSegment} ${
                            styles[category.color]
                          }`}
                          style={{ width: `${category.percentage}%` }}
                          title={`${
                            category.label
                          }: ${category.percentage.toFixed(0)}%`}
                        />
                      )
                  )}
                </div>

                <div className={styles.bpLegend}>
                  {bpCategoryPercentages?.map((category) => (
                    <div key={category.key} className={styles.bpLegendItem}>
                      <div
                        className={`${styles.bpLegendDot} ${
                          styles[category.color]
                        }`}
                      ></div>
                      <div className={styles.bpLegendmid}>
                        <span className={styles.bpLegendLabel}>
                          {category.label}
                        </span>
                        <span className={styles.bpLegendCount}>
                          {" "}
                          ({category.percentage.toFixed(0)}%)
                        </span>

                        {category.drivers && (
                          <div className={styles.bpLegendDrivers}>
                            Sys {category.drivers.systolic}% · Dia{" "}
                            {category.drivers.diastolic}%
                          </div>
                        )}

                        <div className={styles.bpLegendRange}>
                          {category.range}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            <div className={styles.bpSummarySection}>
              <div className={styles.bpSummaryHeader}>
                <h3 className={styles.bpSummaryTitle}>Average Heart Rate</h3>
                <div className={styles.bpAverageValue}>
                  {bpStats?.average?.heart_rate !== null &&
                  bpStats?.average?.heart_rate !== undefined
                    ? Number(bpStats?.average?.heart_rate).toFixed(0)
                    : "--"}{" "}
                  bpm
                </div>
              </div>

              <div className={styles.bpCategoryBar}>
                <div className={styles.bpBarContainer}>
                  {bpHeartRateCategoryPercentages?.map(
                    (category) =>
                      category.percentage > 0 && (
                        <div
                          key={category.key}
                          className={`${styles.bpBarSegment} ${
                            styles[category.color]
                          }`}
                          style={{ width: `${category.percentage}%` }}
                          title={`${
                            category.label
                          }: ${category.percentage.toFixed(0)}%`}
                        />
                      )
                  )}
                </div>

                <div className={styles.bpLegend}>
                  {bpHeartRateCategoryPercentages?.map((category) => (
                    <div key={category.key} className={styles.bpLegendItem}>
                      <div
                        className={`${styles.bpLegendDot} ${
                          styles[category.color]
                        }`}
                      ></div>
                      <div className={styles.bpLegendmid}>
                        <span className={styles.bpLegendLabel}>
                          {category.label}
                        </span>
                        <span className={styles.bpLegendCount}>
                          {" "}
                          ({category.percentage.toFixed(0)}%)
                        </span>

                        <div className={styles.bpLegendRange}>
                          {category.range}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            <div className={styles.bpSourceNoteFull}>{bpStats.source}</div>
          </div>
        );
      case READING_TYPES.WEIGHT:
        if (!weightStats) return null;

        const weightCategoriesMeta = {
          normal: { label: "Normal", range: "BMI 18.5-24.9", color: "normal" },
          overweight: {
            label: "Overweight",
            range: "BMI 25.0-29.9",
            color: "elevated",
          },
          obese: {
            label: "Obese",
            range: "BMI ≥30.0",
            color: "stage1",
          },
        };

        const weightCategoryPercentages = Object.entries(
          weightStats?.categories || {}
        ).map(([key, percentage]) => ({
          key,
          percentage,
          count: Math.round(percentage),
          ...weightCategoriesMeta[key],
        }));

        return (
          <div className={styles.bpSummarySection}>
            <div className={styles.bpSummaryHeader}>
              <h3 className={styles.bpSummaryTitle}>Average Weight</h3>
              <div className={styles.bpAverageValue}>
                {weightStats?.average?.weight !== null
                  ? weightStats?.average?.weight.toFixed(1)
                  : "--"}{" "}
                kg
              </div>
            </div>

            <div className={styles.bpCategoryBar}>
              <div className={styles.bpBarContainer}>
                {weightCategoryPercentages?.map(
                  (category) =>
                    category.percentage > 0 && (
                      <div
                        key={category.key}
                        className={`${styles.bpBarSegment} ${
                          styles[category.color]
                        }`}
                        style={{ width: `${category.percentage}%` }}
                        title={`${
                          category.label
                        }: ${category.percentage.toFixed(0)}%`}
                      />
                    )
                )}
              </div>

              <div className={styles.bpLegend}>
                {weightCategoryPercentages?.map((category) => (
                  <div key={category.key} className={styles.bpLegendItem}>
                    <div
                      className={`${styles.bpLegendDot} ${
                        styles[category.color]
                      }`}
                    ></div>
                    <div className={styles.bpLegendmid}>
                      <span className={styles.bpLegendLabel}>
                        {category.label}
                      </span>
                      <span className={styles.bpLegendCount}>
                        {" "}
                        ({category.percentage.toFixed(0)}%)
                      </span>

                      <div className={styles.bpLegendRange}>
                        {category.range}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.bpSummaryDetails}>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Min:</span>
                <span className={styles.bpDetailValue}>
                  {weightStats?.detailed?.weight.min !== null
                    ? weightStats?.detailed?.weight.min.toFixed(1)
                    : "--"}{" "}
                  kg
                </span>
              </div>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Max:</span>
                <span className={styles.bpDetailValue}>
                  {weightStats?.detailed?.weight.max !== null
                    ? weightStats?.detailed?.weight?.max.toFixed(1)
                    : "--"}{" "}
                  kg
                </span>
              </div>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Readings:</span>
                <span className={styles.bpDetailValue}>
                  {weightStats?.count}
                </span>
              </div>
            </div>

            <div className={styles.bpSourceNote}>{weightStats?.source}</div>
          </div>
        );

      case READING_TYPES.BLOOD_GLUCOSE:
        if (!glucoseStats) return null;

        const glucoseCategoriesMeta = {
          normal: { label: "Normal", range: "<100 mg/dL", color: "normal" },
          prediabetes: {
            label: "Prediabetes",
            range: "100-125 mg/dL",
            color: "elevated",
          },
          diabetes: {
            label: "Diabetes",
            range: "≥126 mg/dL",
            color: "stage1",
          },
        };

        const glucoseCategoryPercentages = Object.entries(
          glucoseStats?.categories || {}
        ).map(([key, percentage]) => ({
          key,
          percentage,
          count: Math.round(percentage),
          ...glucoseCategoriesMeta[key],
        }));

        return (
          <div className={styles.bpSummarySection}>
            <div className={styles.bpSummaryHeader}>
              <h3 className={styles.bpSummaryTitle}>Average Blood Glucose</h3>
              <div className={styles.bpAverageValue}>
                {glucoseStats?.average?.glucose !== null
                  ? glucoseStats?.average?.glucose.toFixed(0)
                  : "--"}{" "}
                mg/dL
              </div>
            </div>

            <div className={styles.bpCategoryBar}>
              <div className={styles.bpBarContainer}>
                {glucoseCategoryPercentages?.map(
                  (category) =>
                    category.percentage > 0 && (
                      <div
                        key={category.key}
                        className={`${styles.bpBarSegment} ${
                          styles[category.color]
                        }`}
                        style={{ width: `${category.percentage}%` }}
                        title={`${
                          category.label
                        }: ${category.percentage.toFixed(0)}%`}
                      />
                    )
                )}
              </div>

              <div className={styles.bpLegend}>
                {glucoseCategoryPercentages?.map((category) => (
                  <div key={category.key} className={styles.bpLegendItem}>
                    <div
                      className={`${styles.bpLegendDot} ${
                        styles[category.color]
                      }`}
                    ></div>
                    <div className={styles.bpLegendmid}>
                      <span className={styles.bpLegendLabel}>
                        {category.label}
                      </span>
                      <span className={styles.bpLegendCount}>
                        {" "}
                        ({category.percentage.toFixed(0)}%)
                      </span>

                      <div className={styles.bpLegendRange}>
                        {category.range}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.bpSummaryDetails}>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Min:</span>
                <span className={styles.bpDetailValue}>
                  {glucoseStats?.detailed?.glucose.min !== null
                    ? glucoseStats?.detailed?.glucose.min.toFixed(0)
                    : "--"}{" "}
                  mg/dL
                </span>
              </div>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Max:</span>
                <span className={styles.bpDetailValue}>
                  {glucoseStats?.detailed?.glucose?.max !== null
                    ? glucoseStats?.detailed?.glucose?.max.toFixed(0)
                    : "--"}{" "}
                  mg/dL
                </span>
              </div>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Readings:</span>
                <span className={styles.bpDetailValue}>
                  {glucoseStats?.count}
                </span>
              </div>
            </div>

            <div className={styles.bpSourceNote}>{glucoseStats?.source}</div>
          </div>
        );

      case READING_TYPES.TEMPERATURE:
        if (!temperatureStats) return null;

        const temperatureCategoriesMeta = {
          normal: { label: "Normal", range: "97.0-99.5°F", color: "normal" },
          low_grade_fever: {
            label: "Low Grade Fever",
            range: "99.5-100.9°F",
            color: "elevated",
          },
          fever: {
            label: "Fever",
            range: "≥101.0°F",
            color: "stage1",
          },
          hypothermia: {
            label: "Hypothermia",
            range: "<97.0°F",
            color: "stage2",
          },
        };

        const temperatureCategoryPercentages = Object.entries(
          temperatureStats?.categories || {}
        ).map(([key, percentage]) => ({
          key,
          percentage,
          count: Math.round(percentage),
          ...temperatureCategoriesMeta[key],
        }));

        return (
          <div className={styles.bpSummarySection}>
            <div className={styles.bpSummaryHeader}>
              <h3 className={styles.bpSummaryTitle}>Average Temperature</h3>
              <div className={styles.bpAverageValue}>
                {temperatureStats?.average?.temperature !== null
                  ? temperatureStats?.average?.temperature.toFixed(1)
                  : "--"}{" "}
                °F
              </div>
            </div>

            <div className={styles.bpCategoryBar}>
              <div className={styles.bpBarContainer}>
                {temperatureCategoryPercentages?.map(
                  (category) =>
                    category.percentage > 0 && (
                      <div
                        key={category.key}
                        className={`${styles.bpBarSegment} ${
                          styles[category.color]
                        }`}
                        style={{ width: `${category.percentage}%` }}
                        title={`${
                          category.label
                        }: ${category.percentage.toFixed(0)}%`}
                      />
                    )
                )}
              </div>

              <div className={styles.bpLegend}>
                {temperatureCategoryPercentages?.map((category) => (
                  <div key={category.key} className={styles.bpLegendItem}>
                    <div
                      className={`${styles.bpLegendDot} ${
                        styles[category.color]
                      }`}
                    ></div>
                    <div className={styles.bpLegendmid}>
                      <span className={styles.bpLegendLabel}>
                        {category.label}
                      </span>
                      <span className={styles.bpLegendCount}>
                        {" "}
                        ({category.percentage.toFixed(0)}%)
                      </span>

                      <div className={styles.bpLegendRange}>
                        {category.range}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.bpSummaryDetails}>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Min:</span>
                <span className={styles.bpDetailValue}>
                  {temperatureStats?.detailed?.temperature?.min !== null
                    ? temperatureStats?.detailed?.temperature?.min.toFixed(1)
                    : "--"}{" "}
                  °F
                </span>
              </div>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Max:</span>
                <span className={styles.bpDetailValue}>
                  {temperatureStats?.detailed?.temperature.max !== null
                    ? temperatureStats?.detailed?.temperature?.max.toFixed(1)
                    : "--"}{" "}
                  °F
                </span>
              </div>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Readings:</span>
                <span className={styles.bpDetailValue}>
                  {temperatureStats?.count}
                </span>
              </div>
            </div>

            <div className={styles.bpSourceNote}>
              {temperatureStats?.source}
            </div>
          </div>
        );

      case READING_TYPES.PULSE_OX:
        if (!pulseOxStats) return null;

        const pulseOxCategoriesMeta = {
          normal: { label: "Normal", range: "95%-100%", color: "normal" },
          mild_hypoxemia: {
            label: "Mild Hypoxemia",
            range: "90%-94%",
            color: "elevated",
          },
          severe_hypoxemia: {
            label: "Severe Hypoxemia",
            range: "<85%",
            color: "stage1",
          },
        };

        const pulseOxCategoryPercentages = Object.entries(
          pulseOxStats?.categories || {}
        ).map(([key, percentage]) => ({
          key,
          percentage,
          count: Math.round(percentage),
          ...pulseOxCategoriesMeta[key],
        }));

        return (
          <div className={styles.bpSummarySection}>
            <div className={styles.bpSummaryHeader}>
              <h3 className={styles.bpSummaryTitle}>Average Pulse Oximetry</h3>
              <div className={styles.bpAverageValue}>
                {pulseOxStats?.average?.oxygen !== null
                  ? pulseOxStats?.average?.oxygen.toFixed(0)
                  : "--"}{" "}
                % SpO₂
              </div>
            </div>

            <div className={styles.bpCategoryBar}>
              <div className={styles.bpBarContainer}>
                {pulseOxCategoryPercentages?.map(
                  (category) =>
                    category.percentage > 0 && (
                      <div
                        key={category.key}
                        className={`${styles.bpBarSegment} ${
                          styles[category.color]
                        }`}
                        style={{ width: `${category.percentage}%` }}
                        title={`${
                          category.label
                        }: ${category.percentage.toFixed(0)}%`}
                      />
                    )
                )}
              </div>

              <div className={styles.bpLegend}>
                {pulseOxCategoryPercentages?.map((category) => (
                  <div key={category.key} className={styles.bpLegendItem}>
                    <div
                      className={`${styles.bpLegendDot} ${
                        styles[category.color]
                      }`}
                    ></div>
                    <div className={styles.bpLegendmid}>
                      <span className={styles.bpLegendLabel}>
                        {category.label}
                      </span>
                      <span className={styles.bpLegendCount}>
                        {" "}
                        ({category.percentage.toFixed(0)}%)
                      </span>

                      <div className={styles.bpLegendRange}>
                        {category.range}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.bpSummaryDetails}>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Min:</span>
                <span className={styles.bpDetailValue}>
                  {pulseOxStats?.detailed?.oxygen.min !== null
                    ? pulseOxStats?.detailed?.oxygen?.min.toFixed(0)
                    : "--"}{" "}
                  %
                </span>
              </div>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Max:</span>
                <span className={styles.bpDetailValue}>
                  {pulseOxStats?.detailed?.oxygen.max !== null
                    ? pulseOxStats?.detailed?.oxygen?.max.toFixed(0)
                    : "--"}{" "}
                  %
                </span>
              </div>
              <div className={styles.bpDetailItem}>
                <span className={styles.bpDetailLabel}>Readings:</span>
                <span className={styles.bpDetailValue}>
                  {pulseOxStats?.count}
                </span>
              </div>
            </div>

            <div className={styles.bpSourceNote}>{pulseOxStats?.source}</div>
          </div>
        );

      default:
        return null;
    }
  }, [
    readingType,
    hasData,
    bpStats,
    weightStats,
    glucoseStats,
    temperatureStats,
    pulseOxStats,
  ]);

  return (
    <div className={styles.chartWrapper} ref={chartContainerRef}>
      <div className={styles.card}>
        <div className={styles.cardContent}>
          {/* Chart Section */}
          <div
            className={styles.chartContainer}
            aria-label={`${readingTypeTitle} chart`}
          >
            {error ? (
              <div className={styles.noDataMessage} role="alert">
                <p>{error}</p>
              </div>
            ) : !hasData ? (
              <EmptyState />
            ) : (
              <div
                ref={chartRef}
                onMouseMove={handleChartMouseMove}
                onMouseLeave={handleChartMouseLeave}
                style={{ position: "relative", width: "100%", height: "100%" }}
              >
                {hoveredEventGroup && (
                  <EventTooltip
                    events={hoveredEventGroup.events}
                    position={eventTooltipPosition}
                  />
                )}
                <ResponsiveContainer width="100%" height={210}>
                  <LineChart
                    data={plotData}
                    onMouseMove={handleLineChartMouseMove}
                    onMouseLeave={() => setIsNearReading(false)}
                    // Extra top margin when there are events, to fit the
                    // event markers above the plot area.
                    margin={{
                      top: eventGroups.length ? 24 : 10,
                      right: 10,
                      left: 0,
                      bottom: 10,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={lineColors.primary}
                      strokeOpacity={0.15}
                    />
                    <XAxis
                      dataKey="timestamp"
                      type="category"
                      tick={{ fill: "#666", fontSize: 10 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                      minTickGap={15}
                    />
                    <YAxis
                      domain={yAxisDomain}
                      tick={{ fill: "#666", fontSize: 10 }}
                    />

                    {/* Reference Areas for Thresholds */}
                    {referenceAreas.map((area) => (
                      <ReferenceArea
                        key={area.key}
                        y1={area.y1}
                        y2={area.y2}
                        fill="#e3f2fd"
                        fillOpacity={0.2}
                      />
                    ))}

                    {/* Threshold Lines */}
                    {thresholdLines.map((line) => (
                      <ReferenceLine
                        key={line.key}
                        y={line.y}
                        stroke="#444444"
                        strokeWidth={1}
                        strokeDasharray="5 5"
                      />
                    ))}

                    {/* Previous-average dotted reference lines */}
                    {previousAverageLines.map((line) => (
                      <ReferenceLine
                        key={line.key}
                        y={line.y}
                        stroke={line.color}
                        strokeWidth={1.5}
                        strokeDasharray="2 4"
                      />
                    ))}

                    {/* Event Lines — one per day's events, on its own
                        category slot at the real date (see plotData above),
                        topped with a diamond marker. */}
                    {eventGroups.map((group) => {
                      const slotX = eventSlotTimestamps[group.id];
                      if (slotX === undefined) return null;
                      const isHovered = hoveredEventGroup?.id === group.id;

                      return (
                        <ReferenceLine
                          key={`event-${group.id}`}
                          x={slotX}
                          stroke="#9c27b0"
                          strokeWidth={isHovered ? 3 : 2}
                          strokeDasharray="5 5"
                          strokeOpacity={isHovered ? 1 : 0.6}
                          label={
                            <EventMarker
                              count={group.events.length}
                              active={isHovered}
                            />
                          }
                        />
                      );
                    })}

                    <Tooltip
                      // Off while an event is hovered (only its tooltip
                      // shows) or the cursor is away from any reading;
                      // otherwise leave recharts in control (undefined).
                      active={hoveredEventGroup || !isNearReading ? false : undefined}
                      content={<CustomTooltip readingType={readingType} showAverages={visibleLines.previousAverages} />}
                    />

                    {/* Primary Line */}
                    {primaryDataKey && visibleLines.primary && (
                      <Line
                        type={lineCurveType}
                        dataKey={primaryDataKey}
                        name={
                          readingType === READING_TYPES.BLOOD_PRESSURE
                            ? "Systolic"
                            : readingType === READING_TYPES.WEIGHT
                            ? "Weight"
                            : readingType === READING_TYPES.TEMPERATURE
                            ? "Temperature"
                            : readingType === READING_TYPES.PULSE_OX
                            ? "Oxygen"
                            : "Glucose"
                        }
                        stroke={lineColors.primary}
                        strokeWidth={2}
                        // Bridge event slots, which carry no reading values.
                        connectNulls
                        dot={
                          !showDots
                            ? false
                            : readingType === READING_TYPES.BLOOD_PRESSURE
                            ? renderBpDot(lineColors.primary)
                            : { r: 3, fill: lineColors.primary, strokeWidth: 0 }
                        }
                        activeDot={(props) => {
                          const { cx, cy, payload } = props;
                          if (cx == null || cy == null) return null;
                          return <circle cx={cx} cy={cy} r={4} fill={getActiveDotColor(payload)} />;
                        }}
                        // Recharts hides Line dots entirely until its draw-in
                        // animation reports finished — so when dots need to
                        // be visible, skip the animation instead of racing it.
                        isAnimationActive={!showDots}
                        animationDuration={500}
                        animationEasing="ease-in-out"
                      />
                    )}

                    {/* Secondary Line */}
                    {secondaryDataKey && visibleLines.secondary && (
                      <Line
                        type={lineCurveType}
                        dataKey={secondaryDataKey}
                        name="Diastolic"
                        stroke={lineColors.secondary}
                        strokeWidth={2}
                        // Bridge event slots, which carry no reading values.
                        connectNulls
                        dot={
                          !showDots
                            ? false
                            : readingType === READING_TYPES.BLOOD_PRESSURE
                            ? renderBpDot(lineColors.secondary)
                            : { r: 3, fill: lineColors.secondary, strokeWidth: 0 }
                        }
                        activeDot={(props) => {
                          const { cx, cy, payload } = props;
                          if (cx == null || cy == null) return null;
                          return <circle cx={cx} cy={cy} r={4} fill={getActiveDotColor(payload)} />;
                        }}
                        isAnimationActive={!showDots}
                        animationDuration={500}
                        animationEasing="ease-in-out"
                      />
                    )}

                    {/* Tertiary Line (Heart Rate) */}
                    {tertiaryDataKey && visibleLines.tertiary && (
                      <Line
                        type={lineCurveType}
                        dataKey={tertiaryDataKey}
                        name="Heart Rate"
                        stroke={lineColors.tertiary}
                        strokeWidth={2}
                        // Bridge event slots, which carry no reading values.
                        connectNulls
                        dot={showDots ? { r: 3, fill: lineColors.tertiary, strokeWidth: 0 } : false}
                        activeDot={(props) => {
                          const { cx, cy, payload } = props;
                          if (cx == null || cy == null) return null;
                          // Color by heart rate itself, not the BP / SpO2
                          // values of the same reading.
                          const color =
                            getHeartRateColor(payload) || lineColors.tertiary;

                          return <circle cx={cx} cy={cy} r={4} fill={color} />;
                        }}
                        isAnimationActive={!showDots}
                        animationDuration={500}
                        animationEasing="ease-in-out"
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Legend Container: a checkbox + label + (i) info popover per
              series. The (i) popover shows the per-series Average &
              Distributions and Min/Max Values. Rendered for every reading
              type from the per-type legendItems descriptor. */}
          {hasData && (
            <div className={styles.legendContainer} aria-label="Chart legend">
              {legendItems.map((item) => (
                <SeriesLegendItem
                  key={item.seriesKey}
                  label={item.label}
                  checkboxColor={item.checkboxColor}
                  visible={visibleLines[item.seriesKey]}
                  onToggle={() => toggleLine(item.seriesKey)}
                  stats={item.seriesKey === "previousAverages" ? null : stats}
                  seriesKey={item.seriesKey}
                  showInfoButton={item.showInfoButton !== false}
                  warning={
                    item.seriesKey === "previousAverages"
                      ? previousAverageWarning
                      : undefined
                  }
                />
              ))}
            </div>
          )}
          {/* Summary Section */}
          {renderSummary()}

          {/* The legacy 4-card grid (Reading Thresholds / Data Lines /
              Averages & Distributions / Min/Max Values) is removed in the
              May 2026 redesign. Threshold visibility now lives in the
              "Set Reading Thresholds" popover (chart panel header), data
              line toggles live in the legend checkboxes above, and the
              stats moved into the per-series (i) popover triggered from
              the legend. */}
        </div>
      </div>
    </div>
  );
};

export default HealthChart;